import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // OAuth redirect: AuthCallback handles its own refresh; skip eager me() here
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }
    setUser(data.user);
    return data.user;
  }, []);

  const verify2FA = useCallback(async (tempToken, code) => {
    const { data } = await api.post("/auth/2fa/login-verify", { temp_token: tempToken, code });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (logoutErr) {
      // Logout failures are non-fatal (e.g. network); still clear client state.
      logger.warn("[auth] Logout request failed; clearing local state anyway:", logoutErr);
    }
    setUser(null);
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard/buyer";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, loginWithGoogle, verify2FA, refresh: fetchMe }),
    [user, loading, login, register, logout, loginWithGoogle, verify2FA, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
